import { IModelDescriptionsClient } from "@multiweb/clients/modelDescriptions";
import { Aggregation, IBaseOWClassDescription, IPublicAttributeInfo, OWAttributeDesciption, OWDataType } from "@multiweb/types/contracts";
import { InvalidArgumentException } from "Global/Base/Exceptions";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { EnumUtils } from "Global/Base/Utils/EnumUtils";
import { ASServices } from "OW.Remoting/Basic/ServiceEndpoints";
import { getCancellationTokenAsRequestContext } from "../../../RequestContext";
import { BaseClient } from "../BaseClient";

const OW_OBJECT_BASE = "OWObjectBase";

/**
 * @internal
 */
export class ModelDescriptionsClient extends BaseClient implements IModelDescriptionsClient {
    private _getClassInfo(className: string): Promise<IOWClassDescriptionDTO> {
        return this._communicationManager.callGroupedPOSTRequest("Classes", ASServices.descriptions, null, className, 100, { resultIDParameterName: "OriginalClassName" });
    }

    public getClassInfo(className: string, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IBaseOWClassDescription> {
        if (!className)
            throw new InvalidArgumentException("className");

        return this._getClassInfo(className).then((desc: IOWClassDescriptionDTO) => tranformClassDescription(desc));
    }

    public getAttributesPublicInfo(className: string, attributes: ReadonlyArray<string>, cancellationToken: CancellationToken = CancellationToken.none): Promise<ReadonlyArray<IPublicAttributeInfo>> {
        return this._communicationManager.callPOSTRequest("Attributes", ASServices.descriptions, { className }, attributes, undefined, getCancellationTokenAsRequestContext(cancellationToken))
            .then((attrInfo: IPublicAttributeInfoDTO[]) => attrInfo.map(item => transformPublicAttributeInfo(item)));
    }
}

// ReSharper disable InconsistentNaming

interface IOWClassDescriptionDTO {
    ClassName: string;
    OriginalClassName: string;
    IsPersistent: boolean;
    SigningAllowed: boolean;
    EncryptingAllowed: boolean;
    VersioningAllowed: boolean;
    SupportsContext: boolean;
    SupportsHierarchy: boolean;
    Attributes: IOWAttributeDescriptionDTO[];
    ChildClasses: Array<string>;
    RelatedClasses: Array<string>;
    PasswordMasterLink: string | null;
}

interface IOWAttributeDescriptionDTO {
    AttributeName: string;
    Type: string;

    CanFilterNull?: boolean;
    CanFilterCompare?: boolean;
    CanFilterEqual?: boolean;
    CanFilterLike?: boolean;
    CanDistinct?: boolean;
    CanGroup?: boolean;
    CanSort?: boolean;

    Required?: boolean;
    ReadOnly?: boolean;
    SigningAllowed?: boolean;
    EncryptingAllowed?: boolean;
    SupportsEncValue?: boolean;
    ClassName?: string;
    Aggregation?: Aggregation;
    MinMultiplicity?: number;
    EnumName?: string;
    Size?: number;
    DisplayAttribute?: string;
}

/** Popis vlastností atributu aplikačního objektu */
interface IPublicAttributeInfoDTO {
    /** Název atributu */
    AttributeName: string;
    /** Název datového typu atributu */
    TypeName: string;
    /** Název interního atributu určeného k zobrazení */
    DisplayAttributeName?: string;
    /** Název datového typu interního atributu určeného k zobrazení */
    DisplayAttributeType?: string;
    /** Formát, který se má použít po zobrazení hodnoty atributu */
    DisplayFormat?: string;
    /** Příznak, zda se má atribut šifrovat */
    EncryptingAlowed?: boolean;
    /** Značí zda šifrovaný atribut opravdu může obsahovat šifrovanou hodnotu */
    SupportsEncValue?: boolean;
    /** Maximální délka hodnoty atributu */
    Size?: number;

    /** Vrací příznak zda-li je atribut grupovatelný */
    CanGroup?: boolean;

    /** Vrací příznak zda-li je atribut tříditelný */
    CanSort?: boolean;

    /** Vrací příznak zda atribut podporuje SELECT DISTINCT */
    CanDistinct?: boolean;

    /** Vrací příznak zda atribut podporuje filtrování IS NULL a IS NOT NULL */
    CanFilterNull?: boolean;
    /** Vrací příznak zda atribut podporuje filtrování porovnáváním */
    CanFilterCompare?: boolean;
    /** Určuje, zda atribut podporuje filtrování rovností */
    CanFilterEqual?: boolean;
    /** Vrací příznak zda atribut podporuje filtrování pomocí operátoru LIKE */
    CanFilterLike?: boolean;

    /** Názvy datových typů použitých při agregaci atributu */
    //public Dictionary<AggregationType, string> AggregationTypeNames;
}
// ReSharper restore InconsistentNaming

const tranformOWAttributesDescription = (attributeDescription: IOWAttributeDescriptionDTO): OWAttributeDesciption => ({
    attributeName: attributeDescription.AttributeName,
    type: attributeDescription.Type === OW_OBJECT_BASE ? OWDataType.OWObject : EnumUtils.getEnumValueFromName(OWDataType, attributeDescription.Type),
    required: attributeDescription.Required,
    readOnly: attributeDescription.ReadOnly,
    signingAllowed: attributeDescription.SigningAllowed,
    encryptingAllowed: attributeDescription.EncryptingAllowed,
    supportsEncValue: attributeDescription.SupportsEncValue,
    displayAttribute: attributeDescription.DisplayAttribute,
    className: attributeDescription.ClassName,
    aggregation: attributeDescription.Aggregation,
    minMultiplicity: attributeDescription.MinMultiplicity,
    enumName: attributeDescription.EnumName,
    size: attributeDescription.Size > 0 ? attributeDescription.Size : undefined,
});

const tranformClassDescription = (classDescription: IOWClassDescriptionDTO): IBaseOWClassDescription => ({
    className: classDescription.ClassName,
    originalClassName: classDescription.OriginalClassName,
    childClasses: classDescription.ChildClasses,
    relatedClasses: classDescription.RelatedClasses,
    isPersistent: classDescription.IsPersistent,
    signingAllowed: classDescription.SigningAllowed,
    encryptingAllowed: classDescription.EncryptingAllowed,
    versioningAllowed: classDescription.VersioningAllowed,
    supportsContext: classDescription.SupportsContext,
    supportsHierarchy: classDescription.SupportsHierarchy,
    passwordMasterLink: classDescription.PasswordMasterLink,
    attributes: classDescription.Attributes.map(attr => tranformOWAttributesDescription(attr)),
});

const transformPublicAttributeInfo = (attrInfo: IPublicAttributeInfoDTO): IPublicAttributeInfo => {
    let type: OWDataType;
    let additionalTypeName: string;

    if (attrInfo.TypeName.indexOf("|") === -1) {
        type = EnumUtils.getEnumValueFromName(OWDataType, attrInfo.TypeName);
    } else {
        type = EnumUtils.getEnumValueFromName(OWDataType, attrInfo.TypeName.split("|")[0]);
        additionalTypeName = attrInfo.TypeName.split("|")[1];
    }

    if (attrInfo.TypeName.startsWith(OW_OBJECT_BASE)) {
        type = OWDataType.OWObject;
    }

    return {
        attributeName: attrInfo.AttributeName,
        type,
        additionalTypeName,
        displayAttributeName: attrInfo.DisplayAttributeName,
        displayAttributeType: attrInfo.DisplayAttributeType,
        displayFormat: attrInfo.DisplayFormat,
        encryptingAlowed: attrInfo.EncryptingAlowed,
        supportsEncValue: attrInfo.SupportsEncValue,
        size: attrInfo.Size,
        canSort: attrInfo.CanSort,
        canFilterNull: attrInfo.CanFilterNull,
        canFilterCompare: attrInfo.CanFilterCompare,
        canFilterEqual: attrInfo.CanFilterEqual,
        canFilterLike: attrInfo.CanFilterLike,
        canGroup: attrInfo.CanGroup,
        canDistinct: attrInfo.CanDistinct,
    };
};
